import apiClient from "@/api-client";
import { useQuery } from "@tanstack/react-query";

/**
 * What a curated voice is picked *from*: the providers set up on this platform, and each
 * provider's own catalogue.
 *
 * Both used to be free text on the curation form, which is how a voice ends up naming a plugin
 * nobody configured or an id the vendor never issued — neither of which fails until an agent is
 * mid-call. Two super-admin reads replace the typing:
 *
 * `GET /admin/voices/providers` — the text-to-speech plugins actually configured and enabled in
 * the current workspace, with the size of each one's catalogue, plus `upload`: the answer for a
 * voice that exists only as a recording, which is offered whether or not anything is configured.
 *
 * `GET /admin/voices/providers/<key>/voices` — that provider's own voices, whole. Served out of
 * the dictionary each plugin syncs when it is saved, so it is a local read rather than a call
 * to the vendor, and it is filtered in the browser rather than a round trip per keystroke.
 *
 * Nothing here reaches a client. What a client is offered is the name a super-admin wrote and
 * the sample somebody recorded — see `use-voices.ts`.
 *
 * Paths never start with `/api` — the axios baseURL already ends in it.
 */

/** A text-to-speech plugin set up in this workspace. */
export interface VoiceProvider {
	/** The plugin's registered name — `elevenlabs`, `in-world-tts`. Stored on the voice. */
	key: string;
	/** The plugin's own title — `ElevenLabs`. What the screen shows. */
	title: string;
	voiceCount: number;
	/** False where the voice id has to be typed for this provider, and `reason` says why. */
	canListVoices: boolean;
	/**
	 * Whether this provider's voices are named by an id at all.
	 *
	 * True of every vendor and false only of `upload`, and the two flags are not the same
	 * question: `canListVoices` is "can we show you the list", this is "is there anything to
	 * point at". A clone has no id anywhere in the world, so the form asks for a recording and
	 * stops asking about the provider's catalogue.
	 */
	requiresVoiceId: boolean;
	/** Whether a sample can be spoken through it, rather than only uploaded. */
	canSynthesise: boolean;
	reason: string | null;
}

export interface VoiceProviders {
	/**
	 * Plugins are configured per workspace and this screen is account-less, so a super-admin can
	 * be here with none selected. That is a different state from "nothing is set up", and the
	 * screen has to say the right one.
	 */
	hasWorkspace: boolean;
	providers: VoiceProvider[];
}

/** One voice out of a provider's catalogue — only what makes it findable in a list of hundreds. */
export interface ProviderVoice {
	/** The provider's own voice id. What lands in the built agent's TTS settings. */
	id: string;
	name: string;
	language: string | null;
	gender: string | null;
	/** Accent, age, use case — whatever the provider gives. Never a sentence. */
	description: string | null;
}

export const voiceProviderKeys = {
	all: ["voice-providers"] as const,
	list: () => [...voiceProviderKeys.all, "list"] as const,
	voices: (provider: string) => [...voiceProviderKeys.all, "voices", provider] as const,
};

/** The providers a voice can be curated against. A 403 here means "not a super-admin". */
export function useVoiceProviders(enabled = true) {
	return useQuery({
		queryKey: voiceProviderKeys.list(),
		queryFn: async (): Promise<VoiceProviders> => {
			const response = await apiClient.get("/admin/voices/providers");
			return response.data;
		},
		enabled,
		// Plugins change when somebody configures one, which is not on the timescale of a drawer
		// being opened and closed a few times.
		staleTime: 5 * 60 * 1000,
	});
}

/**
 * One provider's catalogue.
 *
 * Held far longer than most queries: it is the same list until the plugin is saved again, it is
 * several hundred rows wide, and it is read while somebody is typing into a picker.
 */
export function useProviderVoices(provider: string | null, enabled = true) {
	return useQuery({
		queryKey: voiceProviderKeys.voices(provider ?? ""),
		queryFn: async (): Promise<ProviderVoice[]> => {
			const response = await apiClient.get(`/admin/voices/providers/${encodeURIComponent(provider!)}/voices`);
			return response.data;
		},
		enabled: enabled && Boolean(provider),
		staleTime: 30 * 60 * 1000,
		// A provider that answers 404 is one that is no longer set up. Retrying does not change
		// that, and the form has a typed-id fallback waiting behind the failure.
		retry: false,
	});
}
